import React, { useState } from 'react';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert,
  Stack,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment
} from '@mui/material';
import { Calculate, Info } from '@mui/icons-material';
import { MathFunctions } from '../utils/MathFunctions';
import ResponsiveDiagram from './ResponsiveDiagram';

const HeadRoom: React.FC = () => {
  const [ftf, setFtf] = useState('');
  const [ceilingHeight, setCeilingHeight] = useState('');
  const [rc, setRc] = useState('');
  const [risers, setRisers] = useState('');
  const [opening, setOpening] = useState('');
  const [headroom, setHeadroom] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [calculatedInputs, setCalculatedInputs] = useState<string | null>(null);
  const currentInputs = JSON.stringify([ftf, ceilingHeight, rc, risers, opening]);
  const isStale = calculatedInputs !== null && calculatedInputs !== currentInputs;

  const handleCalculate = () => {
    if (!ftf || !ceilingHeight || !rc || !risers || !opening) {
      setShowError(true);
      return;
    }

    const ftfNum = parseFloat(ftf);
    const ceilingNum = parseFloat(ceilingHeight);
    const rcNum = parseFloat(rc);
    const risersNum = parseFloat(risers);
    const openingNum = parseFloat(opening);

    if (isNaN(ftfNum) || isNaN(ceilingNum) || isNaN(rcNum) || isNaN(risersNum) || isNaN(openingNum)) {
      setShowError(true);
      return;
    }

    setShowError(false);
    setCalculatedInputs(currentInputs);

    // HeadRoom calculation logic from Android app
    const run = rcNum * risersNum + 0.75;
    const lengthA = run - openingNum;
    
    const angleB = Math.atan((ftfNum / risersNum) / rcNum) * 180 / Math.PI;
    const lengthB = MathFunctions.calcLength(lengthA, 90 - angleB, angleB);
    const calculatedHeadroom = (ceilingNum - lengthB) - 0.625;
    const roundedHeadroom = Math.round(calculatedHeadroom * 100) / 100;

    setHeadroom(roundedHeadroom.toString());
  };

  return (
    <Box component="main" id="main-content" tabIndex={-1} sx={{
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #F5F5F5 0%, #E8EAF6 100%)',
      py: { xs: 2, sm: 4 }
    }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Calculate HeadRoom
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Determine headroom clearance for stair openings
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Input Form */}
          <Card component="form" noValidate onSubmit={(event) => { event.preventDefault(); handleCalculate(); }} elevation={3} sx={{ flex: 1, minWidth: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Calculate sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Input Values
                </Typography>
              </Box>

              <Stack spacing={3}>
                {showError && (
                  <Alert severity="error">Enter a number in each highlighted field, then calculate again.</Alert>
                )}
                <TextField
                  fullWidth
                  label="FTF (Floor to Floor)"
                  type="number"
                  value={ftf}
                  error={showError && (!ftf || isNaN(parseFloat(ftf)))}
                  onChange={(e) => setFtf(e.target.value)}
                  inputProps={{ step: "0.01", inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Total floor to floor height"
                />
                
                <TextField
                  fullWidth
                  label="Ceiling Height"
                  type="number"
                  value={ceilingHeight}
                  error={showError && (!ceilingHeight || isNaN(parseFloat(ceilingHeight)))}
                  onChange={(e) => setCeilingHeight(e.target.value)}
                  inputProps={{ step: "0.01", inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Height of ceiling above floor"
                />

                <TextField
                  fullWidth
                  label="RC (Run per Tread)"
                  type="number"
                  value={rc}
                  error={showError && (!rc || isNaN(parseFloat(rc)))}
                  onChange={(e) => setRc(e.target.value)}
                  inputProps={{ step: "0.01", inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Horizontal distance per tread"
                />

                <TextField
                  fullWidth
                  label="Number of Risers"
                  type="number"
                  value={risers}
                  error={showError && (!risers || isNaN(parseFloat(risers)))}
                  onChange={(e) => setRisers(e.target.value)}
                  inputProps={{ step: "1", inputMode: 'numeric' }}
                  helperText="Total number of vertical steps"
                />

                <TextField
                  fullWidth
                  label="Opening Length"
                  type="number"
                  value={opening}
                  error={showError && (!opening || isNaN(parseFloat(opening)))}
                  onChange={(e) => setOpening(e.target.value)}
                  inputProps={{ step: "0.01", inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Length of stair opening"
                />

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  type="submit"
                  startIcon={<Calculate />}
                  sx={{ 
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #1976D2 30%, #42A5F5 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #0D47A1 30%, #1976D2 90%)',
                    }
                  }}
                >
                  Calculate
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Results */}
          <Card component="section" aria-label="Calculation results" aria-live="polite" aria-atomic="true" elevation={3} sx={{ flex: 1 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Info sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Results
                </Typography>
              </Box>

              {isStale && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Inputs changed. These results are from your previous calculation. Calculate again to update them.
                </Alert>
              )}

              {headroom ? (
                <Stack spacing={2}>
                  <Box>
                    <Chip 
                      label="Headroom Clearance" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Headroom Clearance" }}
                      fullWidth
                      value={`${headroom} inches`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#eef7f0',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ mt: 2, p: 2, bgcolor: '#edf5fc', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.primary">
                      <strong>Formula:</strong> (ceiling - lengthB) - 0.625"
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  color: 'text.secondary'
                }}>
                  <Calculate sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1">
                    Enter all values and click Calculate to see headroom clearance
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Diagram */}
        <Card elevation={3} sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
              Reference Diagram
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <ResponsiveDiagram 
              imageName="headroom" 
              alt="Headroom calculation diagram showing FTF, ceiling height, and clearance measurements" 
            />
          </CardContent>
        </Card>

      </Container>
    </Box>
  );
};

export default HeadRoom;